
import { defineComponent, ref, computed, watch, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import ApiService from "@/core/services/ApiService";
import { hasFeature } from "@/core/helpers/feature";
import NewsDashboard from "./Dashboard.vue";
import {
  CaseListResult,
  Facet,
  FacetBucket,
  UpdateItem,
  renderMarkdown,
  formatRelative,
  isRecent,
  normalizeCriticity,
  criticityColor,
  clausesOf,
  joinClauses,
  entityQueryString,
  activeCriticity,
  CRITICITY_LEVELS,
} from "./trf";

export default defineComponent({
  name: "quimerax-news",
  components: { NewsDashboard },
  setup() {
    const route = useRoute();
    const router = useRouter();

    const allowed = computed(() => hasFeature("quimerax-news"));

    // --- URL-derived state (q + page are the whole feed state) ---
    const q = computed(() => String(route.query.q ?? ""));
    const page = computed(() => {
      const raw = parseInt(String(route.query.page ?? "1"), 10);
      return Number.isFinite(raw) && raw >= 1 ? raw : 1;
    });

    // Active tab (feed | dashboard), kept in the URL so it is shareable.
    const tab = computed(() => (route.query.tab === "dashboard" ? "dashboard" : "feed"));
    const setTab = (t: "feed" | "dashboard") => {
      if (t === "dashboard") {
        router.push({ path: "/apps/quimerax-news", query: { tab: "dashboard" } });
      } else {
        router.push({ path: "/apps/quimerax-news", query: q.value ? { q: q.value } : {} });
      }
    };

    const searchInput = ref(q.value);

    // --- Fetched data ---
    const result = ref<CaseListResult | null>(null);
    const updates = ref<UpdateItem[]>([]);
    const isLoading = ref(false);
    const challenge = ref(false);

    const facets = computed<Facet[]>(() => result.value?.facets ?? []);
    const activeClauses = computed(() => clausesOf(q.value));
    const currentCriticity = computed(() => activeCriticity(q.value));
    const criticityOrder = [...CRITICITY_LEVELS].reverse(); // critical..low

    // Facets shown as accumulating chips (everything except criticity).
    const accumulatingFacets = computed(() =>
      facets.value.filter((f) => f.field !== "criticity")
    );

    // --- Facet "show more" ---
    const facetLimit = 8;
    const expandedFacets = ref<string[]>([]);
    const visibleBuckets = (facet: Facet): FacetBucket[] =>
      expandedFacets.value.includes(facet.field)
        ? facet.buckets
        : facet.buckets.slice(0, facetLimit);
    const toggleFacetExpanded = (field: string) => {
      expandedFacets.value = expandedFacets.value.includes(field)
        ? expandedFacets.value.filter((f) => f !== field)
        : [...expandedFacets.value, field];
    };

    // --- Navigation: compose q + page into the URL ---
    const navigate = (nextQ: string, nextPage = 1) => {
      const query: Record<string, string> = {};
      if (nextQ) query.q = nextQ;
      if (nextPage > 1) query.page = String(nextPage);
      router.push({ path: "/apps/quimerax-news", query });
    };

    const submitSearch = () => navigate(searchInput.value.trim(), 1);
    const goToPage = (p: number) => navigate(q.value, p);
    const clearAll = () => {
      searchInput.value = "";
      navigate("", 1);
    };

    const isClauseActive = (field: string, value: string) =>
      activeClauses.value.some(
        (c) => c.toLowerCase() === entityQueryString(field, value).toLowerCase()
      );

    // Add an accumulating facet clause (deduped).
    const applyClause = (field: string, value: string) => {
      const clause = entityQueryString(field, value);
      if (isClauseActive(field, value)) return;
      navigate(joinClauses([...activeClauses.value, clause]), 1);
    };

    const removeClause = (clause: string) => {
      navigate(joinClauses(activeClauses.value.filter((c) => c !== clause)), 1);
    };

    // Criticity is single-select: drop any existing criticity clause first.
    const toggleCriticity = (level: string) => {
      const rest = activeClauses.value.filter((c) => !/^criticity:/i.test(c));
      if (currentCriticity.value !== level) rest.push(`criticity:${level}`);
      navigate(joinClauses(rest), 1);
    };

    // --- Fetching ---
    const fetchCases = async () => {
      if (!allowed.value || tab.value !== "feed") return;
      isLoading.value = true;
      challenge.value = false;
      try {
        const params = new URLSearchParams();
        if (q.value) params.set("q", q.value);
        if (page.value > 1) params.set("page", String(page.value));
        const qs = params.toString();
        const res = await ApiService.get(`/quimerax-news${qs ? "?" + qs : ""}`);
        if (res.data?.challenge_required) {
          challenge.value = true;
          result.value = null;
        } else {
          result.value = res.data;
        }
      } catch (e) {
        // 502 from our proxy => configuration/upstream failure.
        challenge.value = true;
        result.value = null;
      } finally {
        isLoading.value = false;
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    };

    const fetchUpdates = async () => {
      if (!allowed.value) return;
      try {
        const u = await ApiService.get("/quimerax-news/updates?limit=8");
        if (!u.data?.challenge_required) updates.value = u.data?.updates ?? [];
      } catch (e) {
        // Sidebar widget is non-critical; fail quietly.
      }
    };

    // Refetch the feed whenever q/page/tab change; keep the input in sync.
    watch(
      () => [q.value, page.value, tab.value],
      () => {
        searchInput.value = q.value;
        fetchCases();
      }
    );

    onMounted(() => {
      fetchCases();
      fetchUpdates();
    });

    return {
      allowed,
      q,
      tab,
      setTab,
      searchInput,
      result,
      updates,
      isLoading,
      challenge,
      facets,
      accumulatingFacets,
      activeClauses,
      currentCriticity,
      criticityOrder,
      facetLimit,
      expandedFacets,
      visibleBuckets,
      toggleFacetExpanded,
      submitSearch,
      goToPage,
      clearAll,
      applyClause,
      removeClause,
      toggleCriticity,
      isClauseActive,
      // helpers used in template
      renderMarkdown,
      formatRelative,
      isRecent,
      normalizeCriticity,
      criticityColor,
    };
  },
});
